import Image from 'next/image';
import React, { useMemo, useState } from 'react';
import { ErrorMessage, Formik } from 'formik';
import { toast } from 'react-toastify';
import { useCookies } from 'react-cookie';
import Router from 'next/router';

import { cameraImg } from '../../../../public/assets/images';
import { euro, naira, pounds, usd, yen } from '../../../../public/assets/icons';
import {
  editProductFormValidationSchema,
  productPublishFormValidationSchema,
} from '../../../../utils';
import Input from '../../../elements/input';
import CurrencySelect from '../../../elements/select/currency-select';
import DropDown from '../../../elements/select';
import Button from '../../../elements/button';
import TextArea from '../../../elements/textarea';
import Card from '../../../elements/card';
import { deleteIcon, editPen } from '../../../../assets/icons';
import { useQuery } from 'react-query';
import { getProductCategory } from '../../../../api-client/publish-product';
import {
  editProduct,
  getProductDetail,
} from '../../../../api-client/my-product';
import PageLoader from '../../page-loader';
import { capitalize, isValidWebUrl } from '../../../../utils/formatters';
import {
  getItemFromLocalStorage,
  parseJSON,
  setAuthHeader,
} from '../../../../utils/local-storage';

const EditProductModal = ({ setEditProduct, productToken }) => {
  // Fetch Product Details
  const {
    data: productDetails,
    isLoading: isProductLoading,
    isError,
  } = useQuery(
    ['product-detail', productToken],
    () => getProductDetail(Number(productToken)),
    {
      onSuccess: (res) => {
        const {
          productType,
          category,
          currency,
          description,
          name,
          price,
          vendorName,
          image_url,
          approvalType,
        } = res?.data?.data?.product;

        setProductCategory(category);
        setProductType(capitalize(productType));
        setCurrencySelected({
          ...currencySelected,
          url: currency === 'usd' ? usd : null,
          label: currency,
        });
        setPreview(image_url);
        setApprovalMethod(capitalize(approvalType));
      },
    },
  );

  const product = useMemo(
    () => productDetails?.data?.data.product,
    [productDetails],
  );

  // Fetch product categories
  const { data: productCategories, isLoading } = useQuery(
    'product-categories',
    getProductCategory,
  );

  const productTypeData = [
    { label: 'Digital', value: 'Digital' },
    { label: 'Physical', value: 'Physical' },
    { label: 'Service', value: 'Service' },
  ];
  const approveAffiliateBy = [
    { label: 'Automatic', value: 'automatic' },
    { label: 'Manual', value: 'manual' },
  ];

  // Category Dropdown
  const categoryData = useMemo(() => {
    return productCategories?.data?.data?.map((category, index) => ({
      label: category.name,
      value: category.name,
    }));
  }, [productCategories]);

  const currencyData = [{ img: usd, label: 'USD' }];
  const [value, setValue] = useState('');
  const [preview, setPreview] = useState(null);
  const [cookies, setCookies] = useCookies();
  const [approvalMethod, setApprovalMethod] = useState('');
  const [productType, setProductType] = useState('');
  const [currencySelected, setCurrencySelected] = useState({
    url: '',
    label: '',
  });
  const [productCategory, setProductCategory] = useState('');

  const [loading, setLoading] = useState(false);

  const handlePublishProduct = async (data) => {
    const {
      price,
      productName,
      image,
      productDescription,
      marketingResUrl,
      salesPageUrl,
      thankYouPageUrl,
      commission,
    } = data;

    let formData = new FormData();

    formData.set('productType', productType);
    formData.set('price', price);
    formData.set('name', productName);
    formData.set('category', productCategory);
    formData.set('description', productDescription);
    formData.set('commission', commission);
    formData.append('image', image);
    formData.set('approvalType', approvalMethod);
    formData.set('salesPageUrl', salesPageUrl),
      formData.set('thankYouPageUrl', thankYouPageUrl),
      formData.set('marketingResUrl', marketingResUrl);
    formData.set('productToken', productToken);

    setLoading(true);
    setAuthHeader(parseJSON(getItemFromLocalStorage('authToken')));

    if (productType === '') {
      setLoading(false);
      return toast.error('Please select product type');
    }
    if (currencySelected.url === '') {
      setLoading(false);
      return toast.error('Please select a currency');
    }
    if (approvalMethod === '') {
      setLoading(false);
      return toast.error('Please select approval method');
    }
    if (approvalMethod === '') {
      setLoading(false);
      return toast.error('Please select approval method');
    }
    setAuthHeader(cookies.authToken);
    try {
      const res = await editProduct(formData);
      toast.success('Success');

      setLoading(false);
      setEditProduct(false);
    } catch (error) {
      setLoading(false);
      toast.error(error.response.data.message);
    }
  };

  return (
    <section className="h-full w-screen z-50 bg-black flex items-center justify-center fixed top-0 left-0 bg-opacity-40">
      <Card className="w-[1080px]">
        <div className="flex justify-end items-end mb-10">
          <button onClick={() => setEditProduct(false)}>
            <Image src={deleteIcon} alt="Stakecut" />
          </button>
        </div>

        {isProductLoading ? (
          <div className="flex items-center justify-center ">
            <PageLoader
              loadingText="Loading Products Details, Please Wait... 😇 "
              className="h-96 w-96"
            />
          </div>
        ) : isError ? (
          <div className="flex items-center justify-center bg-info py-4 ">
            This product is not yet Approved. Contact Support 😞
          </div>
        ) : (
          <Formik
            // Initialise Initial Form Values
            initialValues={{
              price: product?.price || 50,
              productName: product?.name,
              image: product?.image_url,
              productDescription: product?.description,
              commission: product?.commission,
              marketingResUrl: product?.urls?.marketingResUrl,
              salesPageUrl: product?.urls?.salesPageUrl,
              thankYouPageUrl: product?.urls?.thankYouPageUrl,
            }}
            // Function for Error message Display
            validationSchema={editProductFormValidationSchema}
            // Submit Function
            onSubmit={async (data) => {
              handlePublishProduct(data);
            }}
          >
            {({
              values,
              handleChange,
              handleSubmit,
              errors,
              touched,
              setFieldValue,
            }) => (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
                encType="multipart/form-data"
              >
                <div className="col-span-3 lg:col-span-1 order-1 lg:order-2  lg:items-start mt-3 lg:mt-auto h-full">
                  <div className="flex items-center justify-center">
                    <div
                      className={`h-44 rounded-full relative w-44 bg-gradient-to-t from-slate-700 to-slate-400 overflow-hidden ${
                        errors.image && touched.image
                          ? 'border-2 border-red-400'
                          : null
                      }`}
                    >
                      <Image
                        src={
                          isValidWebUrl(values.image)
                            ? values.image
                            : URL.createObjectURL(values.image)
                        }
                        layout="fill"
                        alt="upload"
                        objectFit="cover"
                      />
                      <div className="absolute">
                        <input
                          type="file"
                          className="h-44 w-44 opacity-0"
                          name="image"
                          accept="image/*"
                          onChange={(e) => {
                            setPreview(e.target.files[0]);
                            setFieldValue('image', e.target.files[0]);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center">
                    <ErrorMessage name="image">
                      {(msg) => (
                        <p className="error-text text-center text-xs">{msg}</p>
                      )}
                    </ErrorMessage>
                  </div>
                </div>

                <div className="lg:col-span-2 col-span-3 order-2 lg:order-1">
                  <div>
                    <label htmlFor="" className="text-base mb-2">
                      Product Name
                    </label>
                    <Input
                      placeholder="Enter Product Name"
                      onChange={handleChange}
                      type="text"
                      name="productName"
                      value={values.productName}
                      error={errors.productName && touched.productName}
                    />
                    <ErrorMessage name="productName">
                      {(msg) => <p className="error-text text-xs">{msg}</p>}
                    </ErrorMessage>
                  </div>
                  <div className="mt-4">
                    <TextArea
                      label="Description"
                      placeholder="Enter Product Description"
                      onChange={handleChange}
                      textAreaName="productDescription"
                      value={values.productDescription}
                      error={
                        errors.productDescription && touched.productDescription
                      }
                    />
                    <ErrorMessage name="productDescription">
                      {(msg) => <p className="error-text text-xs">{msg}</p>}
                    </ErrorMessage>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-6 mt-6">
                    <div>
                      <label htmlFor="" className="text-sm lg:text-base  mb-2">
                        Product Category
                      </label>
                      <DropDown
                        options={categoryData}
                        value={productCategory}
                        selected={productCategory}
                        setSelected={setProductCategory}
                        setValue={setProductCategory}
                      />
                      <ErrorMessage name="productCategory">
                        {(msg) => <p className="error-text text-xs">{msg}</p>}
                      </ErrorMessage>
                    </div>
                    <div>
                      <label htmlFor="" className="text-sm lg:text-base mb-2">
                        Product Type
                      </label>
                      <DropDown
                        options={productTypeData}
                        value={productType}
                        selected={productType}
                        setSelected={setProductType}
                        setValue={setProductType}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2  gap-3 lg:gap-6 mt-6">
                    <div className="col-span-2 lg:col-span-1">
                      <label htmlFor="" className="text-sm lg:text-base mb-2">
                        Currency
                      </label>

                      <Input
                        placeholder="USD"
                        onChange={handleChange}
                        type="text"
                        name="currency"
                        value="USD"
                        disabled
                      />
                    </div>
                    <div className="col-span-2 lg:col-span-1">
                      <label htmlFor="" className="text-sm lg:text-base mb-2">
                        Product Price($)-$1 = N1,200
                      </label>
                      <Input
                        placeholder="Enter Product Price"
                        onChange={handleChange}
                        type="text"
                        name="price"
                        value={values.price}
                        error={errors.price && touched.price}
                      />
                      <ErrorMessage name="price">
                        {(msg) => <p className="error-text text-xs">{msg}</p>}
                      </ErrorMessage>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 lg:col-span-1 gap-3 lg:gap-6  mt-6">
                    <div className="col-span-2 lg:col-span-1">
                      <label htmlFor="" className="text-sm lg:text-base mb-2">
                        Commission Percentage
                      </label>
                      <Input
                        type="number"
                        placeholder="Enter Commission Percentage"
                        onChange={handleChange}
                        name="commission"
                        value={values.commission}
                        error={errors.commission && touched.commission}
                      />
                    </div>
                    <div className="col-span-2 lg:col-span-1">
                      <label htmlFor="" className="text-sm lg:text-base mb-2">
                        How to approve Affiliates
                      </label>
                      <DropDown
                        options={approveAffiliateBy}
                        selected={approvalMethod}
                        setSelected={setApprovalMethod}
                        value={approvalMethod}
                        setValue={setApprovalMethod}
                      />
                    </div>
                  </div>
                  <div className="mt-6">
                    <label htmlFor="" className="text-sm lg:text-base mb-2">
                      URL to Product Sales Page
                    </label>
                    <Input
                      placeholder="Enter URL for Product Sales Page"
                      onChange={handleChange}
                      type="text"
                      name="salesPageUrl"
                      value={values.salesPageUrl}
                      error={errors.salesPageUrl && touched.salesPageUrl}
                    />
                    <ErrorMessage name="salesPageUrl">
                      {(msg) => <p className="error-text text-xs">{msg}</p>}
                    </ErrorMessage>
                  </div>
                  <div className="mt-6">
                    <label htmlFor="" className="text-sm lg:text-base mb-2">
                      URL to Product Download or Thank you page
                    </label>
                    <Input
                      placeholder="Enter URL for Product Download or Thank you page"
                      onChange={handleChange}
                      type="text"
                      name="thankYouPageUrl"
                      value={values.thankYouPageUrl}
                      error={errors.thankYouPageUrl && touched.thankYouPageUrl}
                    />
                    <ErrorMessage name="thankYouPageUrl">
                      {(msg) => <p className="error-text text-xs">{msg}</p>}
                    </ErrorMessage>
                  </div>
                  <div className="mt-6 mb-9">
                    <label htmlFor="" className="text-sm lg:text-base mb-2">
                      URL to Marketing Resources
                    </label>
                    <Input
                      placeholder="Enter the URL of your marketing resources"
                      onChange={handleChange}
                      type="text"
                      name="marketingResUrl"
                      value={values.marketingResUrl}
                      error={errors.marketingResUrl && touched.marketingResUrl}
                    />
                    <ErrorMessage name="marketingResUrl">
                      {(msg) => <p className="error-text text-xs">{msg}</p>}
                    </ErrorMessage>
                  </div>
                  <div className="flex items-center justify-end flex-col md:flex-row flex-wrap">
                    <div className="ml-0 md:ml-2 mt-3 md:mt-0 w-full md:w-64 self-end">
                      <Button
                        label="Save Changes"
                        className="btn-primary w-full  py-5 px-3 border md:w-64"
                        type="submit"
                        loading={loading}
                        onClick={() => console.log('Submitted')}
                      />
                    </div>
                    <div className="ml-0 md:ml-2 mt-3 md:mt-0 w-full md:w-64 self-end">
                      <Button
                        className="border-primary border w-full md:w-64 px-3 py-5 text-primary"
                        label="Cancel"
                        onClick={() => setEditProduct(false)}
                      />
                    </div>
                  </div>
                </div>
              </form>
            )}
          </Formik>
        )}
      </Card>
    </section>
  );
};

export default EditProductModal;
